import { useEffect, useState } from 'react';
import Modal from '../Modal';
import { repositorioCategoriaFinanceira } from '../../sync/repositories/financeiro';
import { extrairMensagensErro } from '../../utils/errorUtils';

/**
 * Modal de cadastro rápido de Categoria Financeira.
 *
 * Props:
 *   isOpen, onClose, onSuccess(novaCategoria)
 *   categoriasMestras — lista [{id, nome, ...}] carregada pela página pai
 *   (a Categoria Mestra é pré-requisito da API; se não houver nenhuma
 *   cadastrada, orienta o usuário a cadastrar uma primeiro na tela de
 *   Categorias Mestras, já que essa entidade está fora do escopo do
 *   cadastro rápido).
 */
export default function ModalCadastroCategoriaFinanceira({ isOpen, onClose, onSuccess, categoriasMestras = [] }) {
  const [nome, setNome] = useState('');
  const [natureza, setNatureza] = useState('entrada');
  const [mestra, setMestra] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setNome('');
      setNatureza('entrada');
      setMestra('');
      setErros([]);
      setSalvando(false);
    }
  }, [isOpen]);

  const handleSalvar = async (e) => {
    e?.preventDefault?.();
    if (!nome.trim()) { setErros(['O nome é obrigatório.']); return; }
    if (!mestra) { setErros(['Selecione a categoria mestra.']); return; }
    setSalvando(true);
    setErros([]);
    try {
      const resp = await repositorioCategoriaFinanceira.criar({
        nome: nome.trim(),
        natureza,
        mestra,
      });
      onSuccess?.(resp.data);
      onClose?.();
    } catch (error) {
      setErros(extrairMensagensErro(error));
    } finally {
      setSalvando(false);
    }
  };

  const mestrasOrdenadas = [...categoriasMestras].sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));

  return (
    <Modal isOpen={isOpen} onClose={salvando ? () => {} : onClose} title="Cadastrar Categoria Financeira">
      <form onSubmit={handleSalvar} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
            placeholder="Ex: Combustível, Manutenção de Máquinas..."
            autoFocus
            disabled={salvando}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Natureza *</label>
          <select
            value={natureza}
            onChange={(e) => setNatureza(e.target.value)}
            disabled={salvando}
            className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
          >
            <option value="entrada">Entrada</option>
            <option value="saida">Saída</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Categoria Mestra *</label>
          {mestrasOrdenadas.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nenhuma categoria mestra cadastrada. Cadastre uma na tela de Categorias Mestras antes de continuar.
            </p>
          ) : (
            <select
              value={mestra}
              onChange={(e) => setMestra(e.target.value)}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">Selecione...</option>
              {mestrasOrdenadas.map((m) => (
                <option key={m.id} value={m.id}>{m.nome}</option>
              ))}
            </select>
          )}
        </div>

        {erros.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 space-y-1">
            {erros.map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={salvando}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={salvando || mestrasOrdenadas.length === 0}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
